import axios from 'axios'
import { offlineStorage } from './offlineStorage'
import { connectionSimulator } from './connectionSimulator'
import type { CurrentCheckInSnapshot } from './offlineStorage'

// Re-export connectionSimulator for components that need it
export { connectionSimulator }

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://app.connecttechusa.com/api/app'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
})

// Add request interceptor to include auth token and block requests in offline simulation
api.interceptors.request.use(
  (config) => {
    // Block all outbound requests when simulating offline or when browser is offline
    if (connectionSimulator.isSimulatingOfflineMode() || !navigator.onLine) {
      const error: any = new Error('Network Error - Request blocked due to offline mode')
      error.code = 'NETWORK_ERROR'
      error.config = config
      error.response = { status: 0, data: { simulated: connectionSimulator.isSimulatingOfflineMode(), reason: !navigator.onLine ? 'offline' : 'simulated' } }
      return Promise.reject(error)
    }

    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
      console.log('🔑 Adding auth token to request:', config.url)
    } else {
      console.log('⚠️ No auth token found for request:', config.url)
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Add response interceptor to handle auth errors and offline simulation
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle offline simulation errors
    if (error.response?.status === 0 && error.response?.data?.simulated) {
      console.log('🌐 Simulated offline error caught by interceptor')
      // Don't redirect to login for simulated offline errors
      return Promise.reject(error)
    }
    // Also handle request-blocked errors from the request interceptor
    if (error.code === 'NETWORK_ERROR' && error.response?.status === 0) {
      return Promise.reject(error)
    }
    
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

// Check-ins API
export const checkinsApi = {
  // Check in to a location
  checkIn: async (data: {
    locationId: string
    latitude: number
    longitude: number
    accuracy?: number
    notes?: string
  }) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        await offlineStorage.storeOfflineAction({
          type: 'checkin',
          locationId: data.locationId,
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy,
          notes: data.notes
        })
        
        return {
          message: 'Check-in stored offline - will sync when connection is restored',
          offline: true,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.post('/checkins/checkin', data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        await offlineStorage.storeOfflineAction({
          type: 'checkin',
          locationId: data.locationId,
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: data.accuracy,
          notes: data.notes
        })
        
        return {
          message: 'Check-in stored offline due to network error - will sync when connection is restored',
          offline: true,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
  
  // Check out from a location
  checkOut: async (locationId: string, data?: { notes?: string }) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Get current location if available for offline checkout
        let latitude = 0
        let longitude = 0
        
        // Try to get current location for offline checkout
        if (navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 60000
              })
            })
            latitude = position.coords.latitude
            longitude = position.coords.longitude
          } catch (error) {
            console.log('Could not get location for offline checkout:', error)
          }
        }
        
        // Store for offline sync
        await offlineStorage.storeOfflineAction({
          type: 'checkout',
          locationId,
          latitude,
          longitude,
          notes: data?.notes
        })
        
        return {
          message: 'Check-out stored offline - will sync when connection is restored',
          offline: true,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.post(`/checkins/checkout/${locationId}`, data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        // Get current location if available for offline checkout
        let latitude = 0
        let longitude = 0
        
        // Try to get current location for offline checkout
        if (navigator.geolocation) {
          try {
            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 5000,
                maximumAge: 60000
              })
            })
            latitude = position.coords.latitude
            longitude = position.coords.longitude
          } catch (error) {
            console.log('Could not get location for offline checkout:', error)
          }
        }
        
        await offlineStorage.storeOfflineAction({
          type: 'checkout',
          locationId,
          latitude,
          longitude,
          notes: data?.notes
        })
        
        return {
          message: 'Check-out stored offline due to network error - will sync when connection is restored',
          offline: true,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
  
  // Get check-in history
  getHistory: (locationId?: string) => 
    api.get('/checkins/history', { params: { locationId } }).then(res => res.data),
  
  // Get current check-in status
  getCurrent: async () => {
    try {
      // If offline or simulating offline, fall back to local snapshot
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      if (isOffline) {
        const snapshot = offlineStorage.getCurrentCheckInSnapshot() as CurrentCheckInSnapshot | null
        if (snapshot) return snapshot
        // If no snapshot, return null-like
        return null
      }
      const res = await api.get('/checkins/current')
      const data = res.data
      // If server reports a current check-in, persist snapshot
      if (data && data.locationId) {
        offlineStorage.setCurrentCheckInSnapshot({
          id: data.id,
          location: data.location,
          locationId: data.locationId,
          checkInTime: data.checkInTime,
          notes: data.notes
        })
      } else {
        offlineStorage.clearCurrentCheckInSnapshot()
      }
      return data
    } catch (error) {
      // On network error, fall back to snapshot
      const snapshot = offlineStorage.getCurrentCheckInSnapshot() as CurrentCheckInSnapshot | null
      return snapshot
    }
  },
  
  // Get offline sync status
  getOfflineStatus: () => ({
    isOnline: navigator.onLine && !connectionSimulator.isSimulatingOfflineMode(),
    pendingSync: offlineStorage.getPendingSyncCount(),
    isSimulatingOffline: connectionSimulator.isSimulatingOfflineMode()
  }),
  
  // Manually trigger sync
  syncOfflineActions: () => offlineStorage.syncOfflineActions(),
  
  // Clear offline data (for testing)
  clearOfflineData: () => offlineStorage.clearOfflineData()
}

// Locations API
export const locationsApi = {
  // Get locations
  getLocations: () => api.get('/locations').then(res => res.data),
}

// Organizations API
export const organizationsApi = {
  // Get organizations
  getOrganizations: () => api.get('/organizations').then(res => res.data),
  
  // Get organization by ID
  getOrganization: (id: string) => api.get(`/organizations/${id}`).then(res => res.data),
  
  // Create organization
  createOrganization: (data: any) => api.post('/organizations', data).then(res => res.data),
  
  // Update organization
  updateOrganization: (id: string, data: any) => api.put(`/organizations/${id}`, data).then(res => res.data),
  
  // Delete organization
  deleteOrganization: (id: string) => api.delete(`/organizations/${id}`).then(res => res.data),
  
  // Get pending invitations
  getPendingInvitations: () => api.get('/organizations/invitations/pending').then(res => res.data),
  
  // Accept invitation
  acceptInvitation: (invitationId: string) => api.post(`/organizations/invitations/${invitationId}/accept`).then(res => res.data),
  
  // Reject invitation
  rejectInvitation: (invitationId: string) => api.post(`/organizations/invitations/${invitationId}/reject`).then(res => res.data),
}

// Reports API
export const reportsApi = {
  // Get incident reports
  getIncidentReports: (params?: any) =>
    api.get('/reports/incidents', { params }).then(res => res.data),
  
  // Get incident report by ID
  getIncidentReportById: (id: string) =>
    api.get(`/incidents/${id}`).then(res => res.data),
  
  // Get hourly report by ID
  getHourlyReportById: (id: string) => {
    console.log('🔍 getHourlyReportById called with id:', id)
    return api.get(`/reports/hourly/${id}`).then(res => {
      console.log('📊 getHourlyReportById response:', res.data)
      return res.data
    }).catch(error => {
      console.error('❌ getHourlyReportById error:', error)
      throw error
    })
  },
  
  // Download hourly report PDF
  downloadHourlyReportPdf: (id: string) => 
    api.get(`/reports/hourly/${id}/pdf`, { responseType: 'blob' }).then(res => res.data),
  
  // Create incident report
  createIncidentReport: async (data: any) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'incident',
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Incident report stored offline - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.post('/reports/incidents', data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'incident',
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Incident report stored offline due to network error - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
  
  // Get hourly reports
  getHourlyReports: (params?: any) => {
    console.log('🔍 getHourlyReports called with params:', params)
    return api.get('/reports/hourly', { params }).then(res => {
      console.log('📊 getHourlyReports response:', res.data)
      return res.data
    }).catch(error => {
      console.error('❌ getHourlyReports error:', error)
      throw error
    })
  },
  
  // Create hourly report
  createHourlyReport: async (data: any) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Hourly report stored offline - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.post('/reports/hourly', data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Hourly report stored offline due to network error - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
  
  // Get organization irregularities
  getOrganizationIrregularities: () =>
    api.get('/reports/irregularities').then(res => res.data),
  
  // Get dashboard stats
  getDashboardStats: () =>
    api.get('/reports/dashboard').then(res => res.data),
  
  // Get reports for current shift
  getCurrentShiftReports: () =>
    api.get('/reports/current-shift').then(res => res.data),
  
  // Get incident chart data
  getIncidentChartData: (params: {
    startDate: string
    endDate: string
    periodicity: 'daily' | 'weekly' | 'monthly'
    locationId?: string
  }) => api.get('/reports/incident-chart-data', { params }).then(res => res.data),

  // Draft incident reports
  getDraftIncidentReports: () =>
    api.get('/reports/incidents/drafts').then(res => res.data),
  
  updateIncidentReportDraft: async (reportId: string, data: any) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'incident',
          reportId,
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Incident report draft updated offline - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.put(`/reports/incidents/drafts/${reportId}`, data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'incident',
          reportId,
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Incident report draft updated offline due to network error - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
  
  publishIncidentReport: (reportId: string) =>
    api.post(`/reports/incidents/drafts/${reportId}/publish`).then(res => res.data),

  deleteIncidentReportDraft: (reportId: string) =>
    api.delete(`/reports/incidents/drafts/${reportId}`).then(res => res.data),

  deleteAllIncidentReportDrafts: () =>
    api.delete('/reports/incidents/drafts').then(res => res.data),

  // Create incident report via reports API (supports drafts with relaxed validation)
  createIncidentReportViaReports: async (data: any) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'incident',
          data,
          mediaFiles: []
        })
        
        return {
          message: 'Incident report stored offline - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.post('/reports/incidents', data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'incident',
          data,
          mediaFiles: []
        })
        
        return {
          message: 'Incident report stored offline due to network error - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },

  // Draft hourly reports
  getDraftHourlyReports: () => {
    console.log('🔍 getDraftHourlyReports called')
    return api.get('/reports/hourly/drafts').then(res => {
      console.log('📊 getDraftHourlyReports response:', res.data)
      return res.data
    }).catch(error => {
      console.error('❌ getDraftHourlyReports error:', error)
      throw error
    })
  },
  
  updateHourlyReportDraft: async (reportId: string, data: any) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          reportId,
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Hourly report draft updated offline - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.put(`/reports/hourly/drafts/${reportId}`, data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          reportId,
          data: { ...data, isDraft: true },
          mediaFiles: []
        })
        
        return {
          message: 'Hourly report draft updated offline due to network error - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
  
  publishHourlyReport: async (reportId: string) => {
    try {
      console.log('📤 Publishing hourly report draft:', reportId)
      const response = await api.post(`/reports/hourly/drafts/${reportId}/publish`)
      console.log('✅ Hourly report published successfully:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ Error publishing hourly report:', error)
      console.error('❌ Error response:', error.response?.data)
      throw error
    }
  },

  deleteHourlyReportDraft: (reportId: string) =>
    api.delete(`/reports/hourly/drafts/${reportId}`).then(res => res.data),

  deleteAllHourlyReportDrafts: () =>
    api.delete('/reports/hourly/drafts').then(res => res.data),

  // Create hourly report via reports API (supports drafts with relaxed validation)
  createHourlyReportViaReports: async (data: any) => {
    try {
      // Check if we're offline or simulating offline
      const isOffline = !navigator.onLine || connectionSimulator.isSimulatingOfflineMode()
      
      if (isOffline) {
        // Store for offline sync
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          data,
          mediaFiles: []
        })
        
        return {
          message: 'Hourly report stored offline - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      
      // Try to make the API call
      const response = await connectionSimulator.simulateNetworkError(
        api.post('/reports/hourly', data)
      )
      return response.data
    } catch (error: any) {
      // If network error, store offline
      if (error.message?.includes('Network Error') || error.code === 'NETWORK_ERROR') {
        const localId = await offlineStorage.storeOfflineReportDraft({
          type: 'hourly',
          data,
          mediaFiles: []
        })
        
        return {
          message: 'Hourly report stored offline due to network error - will sync when connection is restored',
          offline: true,
          localId,
          pendingSync: offlineStorage.getPendingSyncCount()
        }
      }
      throw error
    }
  },
}

// Notifications API
export const notificationsApi = {
  getAll: (params?: { page?: number; limit?: number }) => {
    console.log('🌐 API: Getting notifications from /notifications')
    return api.get('/notifications', { params }).then(res => {
      console.log('🌐 API: Notifications response:', res.data)
      return res.data
    })
  },
  
  markAsRead: (id: string) => {
    console.log(`🌐 API: Marking notification ${id} as read via PUT /notifications/${id}/read`)
    return api.put(`/notifications/${id}/read`).then(res => {
      console.log('🌐 API: Mark as read response:', res.data)
      return res.data
    })
  },
  
  markAllAsRead: () => {
    console.log('🌐 API: Marking all notifications as read via PUT /notifications/read-all')
    return api.put('/notifications/read-all').then(res => {
      console.log('🌐 API: Mark all as read response:', res.data)
      return res.data
    })
  },
}

// Incidents API
export const incidentsApi = {
  // Get incident types for an organization (includes severity info)
  getIncidentTypes: (organizationId: string) =>
    api.get('/incidents/types', { params: { organizationId } }).then(res => res.data),
  
  // Get global incident severities (read-only)
  getIncidentSeverities: () =>
    api.get('/incidents/severities').then(res => res.data),
  
  // Create incident report
  createIncidentReport: (data: any) =>
    api.post('/incidents', data).then(res => res.data),
  
  // Download incident PDF
  downloadIncidentPdf: (id: string) => 
    api.get(`/incidents/${id}/pdf`, { responseType: 'blob' }).then(res => res.data),
  
  // Download incident list PDF
  downloadIncidentListPdf: (params: {
    organizationId: string
    locationId: string
    startDate: string
    endDate: string
  }) => api.get('/incidents/list/pdf', { 
    params,
    responseType: 'blob' 
  }).then(res => res.data),
}

// Post Orders API
export const postOrdersApi = {
  // Get post orders
  getPostOrders: () => api.get('/post-orders/user').then(res => res.data),
  
  // Get post orders for a specific location
  getPostOrdersByLocation: (locationId: string) => 
    api.get(`/locations/${locationId}/post-orders`).then(res => res.data),
  
  // Sign post order
  signPostOrder: (orderId: string, signatureData: any) =>
    api.post(`/post-orders/${orderId}/sign`, signatureData).then(res => res.data),
}

// Auth API
export const authApi = {
  // Get user profile
  getProfile: () => api.get('/auth/profile').then(res => res.data),
  
  // Create user profile
  createProfile: (data: { email: string; name: string; pictureUrl?: string }) =>
    api.post('/auth/profile', data).then(res => res.data),
  
  // Update user profile
  updateProfile: (data: { name?: string; email?: string; pictureUrl?: string }) =>
    api.put('/auth/profile', data).then(res => res.data),
  
  // Upload photo
  uploadPhoto: (formData: FormData) => {
    console.log('🌐 API: Uploading photo with FormData:', formData)
    return api.post('/auth/upload-photo', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(res => {
      console.log('🌐 API: Upload photo response:', res.data)
      return res.data
    }).catch(error => {
      console.error('🌐 API: Upload photo error:', error)
      throw error
    })
  },
  
  // Select organization
  selectOrganization: (organizationId: string) =>
    api.post('/auth/select-organization', { organizationId }).then(res => res.data),
  
  // Accept invitation
  acceptInvitation: (token: string) =>
    api.post('/auth/accept-invitation', { token }).then(res => res.data),
}

// Support API
export const supportApi = {
  // Create support ticket
  createTicket: async (data: {
    subject: string
    description: string
    priority: 'LOW' | 'MEDIUM' | 'HIGH'
    userEmail: string
    userName: string
    organizationId: string
  }) => api.post('/support/tickets', data).then(res => res.data),

  // Get support tickets for current user
  getTickets: () => api.get('/support/tickets').then(res => res.data),

  // Get support ticket by ID
  getTicketById: (id: string) => api.get(`/support/tickets/${id}`).then(res => res.data),

  // Respond to support ticket
  respondToTicket: async (ticketId: string, message: string) => 
    api.post(`/support/tickets/${ticketId}/respond`, { message }).then(res => res.data),

  // Get support stats
  getStats: () => api.get('/support/stats').then(res => res.data)
}

export default api